import { useCapabilities } from '@/features/authentication/hooks/useCapabilities';
import { CalibrationDeviceRailRow } from '@/features/calibration/components/CalibrationDeviceRailRow';
import {
  calibrationDeviceRailListVariants,
  calibrationDeviceRailScrollVariants,
} from '@/features/calibration/config/calibrationDeviceRail';
import { useCalibrationDeviceTree } from '@/features/calibration/hooks/useCalibrationDeviceTree';
import {
  calibrationDeviceTreeRowEstimate,
  visibleCalibrationDevicePaginationCandidate,
} from '@/features/calibration/lib/calibrationDeviceTree';
import { useCalibrationStations } from '@/features/calibration/services/calibrationQueries';
import { NavSectionLabel } from '@/shared/components/AppSidebar/NavSectionLabel';
import { ListNextPageFooter } from '@/shared/components/ListStatus';
import { Button } from '@/shared/components/ui/button';
import { useVirtualScrollMargin } from '@/shared/hooks/useVirtualScrollMargin';
import { openDialog } from '@/shared/lib/dialogRegistry';
import { flattenInfiniteItems, shouldFetchNextVirtualPage } from '@/shared/lib/infiniteQuery';
import { Trans, useLingui } from '@lingui/react/macro';
import { useParams } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Plus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

export function CalibrationDevicesRail() {
  const { t } = useLingui();
  const { hasCapability } = useCapabilities();
  const { deviceId: activeDeviceId } = useParams({ strict: false });
  const stationsQuery = useCalibrationStations();
  const stations = useMemo(() => flattenInfiniteItems(stationsQuery.data), [stationsQuery.data]);
  const [expandedStationIds, setExpandedStationIds] = useState<Set<string>>(() => new Set());
  const { rows, getDeviceQuery, retryDeviceBranch, fetchNextDevicePage } = useCalibrationDeviceTree(
    stations,
    expandedStationIds,
  );
  const scrollRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const scrollMargin = useVirtualScrollMargin(scrollRef, listRef, rows.length);
  const getRowKey = useCallback((index: number) => rows[index]?.key ?? index, [rows]);
  const virtualizer = useVirtualizer<HTMLElement, HTMLLIElement>({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: getRowKey,
    estimateSize: (index) => calibrationDeviceTreeRowEstimate(rows[index]),
    initialRect: { width: 280, height: 640 },
    onChange: (instance, isScrolling) => {
      if (
        shouldFetchNextVirtualPage(
          instance,
          isScrolling,
          rows.length,
          stationsQuery.hasNextPage,
          stationsQuery.isFetchingNextPage,
          stationsQuery.isFetchNextPageError,
        )
      ) {
        void stationsQuery.fetchNextPage();
      }
      fetchNextDevicePage(
        visibleCalibrationDevicePaginationCandidate(rows, instance.getVirtualItems()),
        isScrolling,
      );
    },
    overscan: 6,
    scrollMargin,
  });
  const toggleStation = (stationId: string) => {
    setExpandedStationIds((current) => {
      const next = new Set(current);
      if (next.has(stationId)) next.delete(stationId);
      else next.add(stationId);
      return next;
    });
  };

  return (
    <nav className="flex min-h-0 flex-1 flex-col" aria-label={t`Calibration devices`}>
      <div className="flex shrink-0 items-center justify-between px-3 py-3">
        <NavSectionLabel size="compact">
          <Trans>Calibration devices</Trans>
        </NavSectionLabel>
        {hasCapability('calibration.manage') ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t`Add binding`}
            onClick={() => openDialog('create-device-station-binding', {})}
          >
            <Plus className="size-3.5" aria-hidden />
          </Button>
        ) : null}
      </div>
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: this is the rail's sole scroll viewport. */}
      <section ref={scrollRef} tabIndex={0} className={calibrationDeviceRailScrollVariants()}>
        <ul
          ref={listRef}
          className={calibrationDeviceRailListVariants()}
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            if (!row) return null;
            return (
              <CalibrationDeviceRailRow
                key={row.key}
                row={row}
                activeDeviceId={activeDeviceId}
                onToggleStation={toggleStation}
                onRetryDeviceBranch={retryDeviceBranch}
                deviceQuery={
                  row.kind === 'device-footer' ? getDeviceQuery(row.stationId) : undefined
                }
                measureRef={virtualizer.measureElement}
                virtualIndex={item.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${item.start - scrollMargin}px)`,
                }}
              />
            );
          })}
        </ul>
        <ListNextPageFooter query={stationsQuery} />
      </section>
    </nav>
  );
}
